import { AppHTTPService } from '@/api/http-service'
import {
  connectionManager,
  // projectConnectionManager,
} from '@/api/sse/prepare-chat'
import { FieldsContext } from '@/contexts/FieldsContext'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useRef } from 'react'
import {
  AgentStatus,
  ChatItem,
  ChatItemType,
  NormalizedEvent,
  OperationItem,
} from './types'
import { useRulesContext } from '@/contexts/RulesContext'

/**
 * Normalizes events from different sources into a consistent format
 */
function normalizeEvent(event: any): NormalizedEvent {
  const timestamp = Date.now()
  const id =
    event.id ||
    `event-${timestamp}-${Math.random().toString(36).substring(2, 9)}`
  // Handle message:created events
  if (event.event === 'message:created' && event.data) {
    const message = {
      type: 'message',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
    console.log('message:created', message)
    return message
  }
  // Handle message:deleted events
  if (event.event === 'message:deleted' && event.data) {
    return {
      type: 'message-deleted',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Handle message:updated events
  if (event.event === 'message:updated' && event.data) {
    return {
      type: 'message-updated',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Handle thread state updates
  if (event.event === 'thread:updated' && event.data) {
    return {
      type: 'thread',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Handle thread state updates
  if (event.event === 'sheet:updated' && event.data) {
    return {
      type: 'sheet',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Handle record events
  if (event.event === 'record:updated' && event.data) {
    return {
      type: 'record',
      id: event.data.id || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Handle artifact events
  if (event.event === 'artifact:updated' && event.data) {
    return {
      type: 'artifact:updated',
      id: event.data.artifactId || id,
      data: event.data,
      timestamp: timestamp,
    }
  }
  // Handle artifact events
  if (event.event === 'artifact:created' && event.data) {
    return {
      type: 'artifact:created',
      id: event.data.artifactId || id,
      data: event.data,
      timestamp: timestamp,
    }
  }

  // Default case - just wrap the event in our normalized format
  return {
    type: event.type || 'unknown',
    id: id,
    data: event,
    timestamp: timestamp,
  }
}

/**
 * Hook for managing events and connections
 */
function useEventProcessing({
  threadId,
  projectId,
  setStatus,
  setIsLoading,
  addChatItem,
  updateChatItem,
  updateOperation,
  updateRulesFromState,
  setStatusText,
  removeChatItem,
  showAssistantMessages,
}: {
  threadId: string | null
  projectId?: string
  setStatus: (status: AgentStatus) => void
  setIsLoading: (loading: boolean) => void
  addChatItem: (item: ChatItem | ChatItem[]) => void
  removeChatItem: (id: string) => void
  updateChatItem: (id: string, updates: Partial<ChatItem>) => void
  updateOperation: (operation: OperationItem) => void
  updateRulesFromState: (rules: any[]) => void
  setStatusText: (text: string) => void
  showAssistantMessages: boolean
}) {
  const { columnConfig } = useContext(FieldsContext)
  const {
    pendingRules: rules,
    addPendingRules,
    clearPendingRules,
  } = useRulesContext()
  const threadStreamConnectedRef = useRef<{
    connected: boolean
    threadId: string | null
  }>({
    connected: false,
    threadId: null,
  })

  const projectStreamConnectedRef = useRef<{
    connected: boolean
    projectId: string | null
  }>({
    connected: false,
    projectId: null,
  })

  const highestRecordProcessedRef = useRef<{
    number: number
    timestamp: number
  } | null>(null)

  const queryClient = useQueryClient()

  // Add a ref to track processed message IDs to prevent duplicates
  const processedEventIds = useRef<Set<string>>(new Set())

  // Track existing status items by taskId
  const existingStatusItems = useRef<Map<string, string>>(new Map())

  /**
   * Processes incoming events from the thread stream
   */
  const handleEvent = useCallback(
    async (rawEvent: any) => {
      // Normalize the event format for easier processing
      console.log('handleEvent', rawEvent)
      const event = normalizeEvent(rawEvent)

      switch (event.type) {
        case 'record':
          const recordData = event.data
          window.postMessage({
            type: 'updateBufferedRecord',
            dataRowIndex: recordData.dataRowIndex,
            recordId: recordData.recordId,
            values: recordData.values,
          })
          if (recordData.progress) {
            const totalRecords = recordData.progress.total
            const currentRecord = recordData.progress.current
            const now = Date.now()
            // Only update the status text if the record number is higher than the
            // previous highest record number or if the timestamp is more than 5 seconds old
            if (
              highestRecordProcessedRef.current === null ||
              currentRecord > highestRecordProcessedRef.current.number ||
              (currentRecord < highestRecordProcessedRef.current.number &&
                now - highestRecordProcessedRef.current.timestamp > 5000)
            ) {
              highestRecordProcessedRef.current = {
                number: currentRecord,
                timestamp: now,
              }

              const displayRecordNumber =
                highestRecordProcessedRef.current?.number || currentRecord

              setStatusText(
                `Record updated (${displayRecordNumber} of ${totalRecords})`
              )
            }

            if (recordData.progress.current === recordData.progress.total) {
              addChatItem({
                id: 'record-update-complete',
                type: 'message',
                content: 'All done',
                role: 'assistant',
                timestamp: event.timestamp,
              })
            }
          }
          break

        case 'sheet':
          // When a sheet is updated, create a new message to inform the user
          const sheetData = event.data

          if (sheetData.rules) {
            addPendingRules(
              sheetData.rules.filter((rule) => rule.disabled !== true)
            )
          }

          if (sheetData?.metadata?.analysis) {
            const analysis = sheetData.metadata.analysis[0]
            addChatItem({
              id: 'initial-analysis',
              type: 'sheet-analysis',
              content: analysis.content,
              role: 'assistant',
              timestamp: new Date(analysis.createdAt).getTime(),
            })

            const suggestions = await AppHTTPService.client.agent.suggestReroll(
              {
                sheetId: sheetData.id,
                threadId: threadId,
                userPrompt: analysis.content,
                messageId: undefined,
                columnConfig,
              }
            )

            const { assessment, options } = suggestions.data

            // First add the assessment message
            const assessmentMessage: ChatItem = {
              id: `initial-analysis-assessment`,
              type: 'message',
              content: assessment,
              role: 'assistant',
              timestamp: event.timestamp,
            }

            // Then add the options message
            const optionsMessage: ChatItem = {
              id: `initial-analysis-options`,
              type: 'options',
              content: options,
              options: options.map((option: any) => ({
                id: option.id,
                label: option.title,
                description: option.description,
                action: option.action,
                confidence: option.confidence,
              })),
              sheetId: sheetData.id, // Include the sheet ID with the options
              role: 'assistant',
              timestamp: event.timestamp,
            }
            setStatusText(`We have some fresh suggestions!`)
            addChatItem([assessmentMessage, optionsMessage])
            console.log('suggestions', suggestions)
          }

          if (sheetData.sheet) {
            const sheetDataResult = { data: sheetData.sheet }
            queryClient.setQueryData(['sheets', sheetData.id], sheetDataResult)
            window.postMessage({
              type: 'refetchRecords',
              sheetId: sheetData.id,
            })
          } else {
            queryClient.invalidateQueries({
              queryKey: ['sheets', sheetData.id],
            })
          }

          if (sheetData.rules) {
            updateRulesFromState(sheetData.rules)
          }

          setStatusText(`Sheet updated: ${sheetData.name || 'Unnamed'}`)
          setIsLoading(false)
          break

        case 'message-deleted':
          removeChatItem(event.data.id)
          break

        case 'message-updated':
          // Handle message updates, particularly for status messages
          const updatedMessageData = event.data
          const updatedRole =
            typeof updatedMessageData.role === 'string'
              ? updatedMessageData.role.toLowerCase()
              : 'assistant'

          let updatedContentText = ''
          if (updatedMessageData.content) {
            if (typeof updatedMessageData.content === 'string') {
              updatedContentText = updatedMessageData.content
            } else if (updatedMessageData.content.text) {
              updatedContentText = updatedMessageData.content.text
            } else {
              updatedContentText = updatedMessageData.content
            }
          }

          // Check if this is an agent message that needs special handling
          if (
            updatedMessageData.metadata?.source === 'improv-agent' ||
            updatedMessageData.metadata?.source === 'conversational-agent'
          ) {
            console.log(
              '📨 Agent message update detected in eventProcessing:',
              {
                type:
                  updatedMessageData.metadata?.type || updatedMessageData.type,
                content: updatedContentText,
                metadata: updatedMessageData.metadata,
              }
            )

            // For agent messages, we'll update them in chat items
            if (
              updatedContentText &&
              updatedMessageData.threadId === threadId
            ) {
              const updatedMessageType =
                updatedMessageData.metadata?.messageType ||
                updatedMessageData.type ||
                'message'

              const updatedAgentChatItem: ChatItem = {
                id: updatedMessageData.id || event.id,
                type: (['summary', 'progress', 'results', 'status'].includes(
                  updatedMessageType
                )
                  ? updatedMessageType
                  : 'message') as ChatItemType,
                content: updatedContentText,
                role: updatedRole,
                timestamp: event.timestamp,
                metadata: updatedMessageData.metadata,
                // Include additional content for summary messages
                ...(updatedMessageType === 'summary' && {
                  suggestions: updatedMessageData.content?.suggestions,
                  needsFeedback: updatedMessageData.content?.needsFeedback,
                  feedbackQuestion:
                    updatedMessageData.content?.feedbackQuestion,
                }),
                // Include progress-specific fields
                ...(updatedMessageType === 'progress' && {
                  progress: updatedMessageData.content?.progress,
                  stage: updatedMessageData.content?.stage,
                  agentName: updatedMessageData.content?.agentName,
                }),
                // Include results-specific fields
                ...(updatedMessageType === 'results' && {
                  mappingRules: updatedMessageData.content?.mappingRules,
                  answer: updatedMessageData.content?.answer,
                  classification: updatedMessageData.content?.classification,
                }),
                // Include status-specific fields
                ...(updatedMessageType === 'status' && {
                  statuses: updatedMessageData.content?.statuses,
                  taskId: updatedMessageData.content?.taskId,
                }),
              }

              console.log('Updating chat item with:', updatedAgentChatItem)
              updateChatItem(updatedAgentChatItem.id, updatedAgentChatItem)
            }
          } else {
            // Regular message update handling
            if (
              updatedContentText &&
              (updatedMessageData.role !== 'assistant' ||
                showAssistantMessages) &&
              updatedMessageData.threadId === threadId
            ) {
              const itemId = updatedMessageData.id || event.id
              updateChatItem(itemId, {
                type: 'message',
                content: updatedContentText,
                role: updatedRole,
                timestamp: event.timestamp,
              })
            }
          }
          break

        case 'message':
          const messageData = event.data
          const role =
            typeof messageData.role === 'string'
              ? messageData.role.toLowerCase()
              : 'assistant'

          let contentText = ''
          if (messageData.content) {
            if (typeof messageData.content === 'string') {
              contentText = messageData.content
            } else if (messageData.content.text) {
              contentText = messageData.content.text
            } else {
              contentText = messageData.content
            }
          }

          // Check if this is an agent message that needs special handling
          if (
            messageData.metadata?.source === 'improv-agent' ||
            messageData.metadata?.source === 'conversational-agent'
          ) {
            console.log('📨 Agent message detected in eventProcessing:', {
              type: messageData.metadata?.type || messageData.type,
              content: contentText,
              metadata: messageData.metadata,
            })

            // For agent messages, we'll add them to chat items
            if (contentText && messageData.threadId === threadId) {
              const messageType =
                messageData.metadata?.messageType ||
                messageData.type ||
                'message'

              const agentChatItem: ChatItem = {
                id: messageData.id || event.id,
                type: (['summary', 'progress', 'results', 'status'].includes(
                  messageType
                )
                  ? messageType
                  : 'message') as ChatItemType,
                content: contentText,
                role,
                timestamp: event.timestamp,
                metadata: messageData.metadata,
                // Include additional content for summary messages
                ...(messageType === 'summary' && {
                  suggestions: messageData.content?.suggestions,
                  needsFeedback: messageData.content?.needsFeedback,
                  feedbackQuestion: messageData.content?.feedbackQuestion,
                }),
                // Include progress-specific fields
                ...(messageType === 'progress' && {
                  progress: messageData.content?.progress,
                  stage: messageData.content?.stage,
                  agentName: messageData.content?.agentName,
                }),
                // Include results-specific fields
                ...(messageType === 'results' && {
                  mappingRules: messageData.content?.mappingRules,
                  answer: messageData.content?.answer,
                  classification: messageData.content?.classification,
                }),
                // Include status-specific fields
                ...(messageType === 'status' && {
                  statuses: messageData.content?.statuses,
                  taskId: messageData.content?.taskId,
                }),
              }

              // For status messages, check if one already exists with this ID
              if (messageType === 'status') {
                const existingStatusItemId = existingStatusItems.current.get(
                  agentChatItem.id
                )
                console.log('existingStatusItemId', existingStatusItemId)

                if (existingStatusItemId) {
                  console.log('Updating existing status item', agentChatItem)
                  updateChatItem(agentChatItem.id, agentChatItem)
                } else {
                  addChatItem(agentChatItem)
                  existingStatusItems.current.set(
                    agentChatItem.id,
                    agentChatItem.id
                  )
                }
              } else {
                addChatItem(agentChatItem)
              }
            }
          } else {
            // Regular message handling
            if (
              contentText &&
              (messageData.role !== 'assistant' || showAssistantMessages) &&
              messageData.threadId === threadId
            ) {
              addChatItem({
                id: messageData.id || event.id,
                type: 'message',
                content: contentText,
                role,
                timestamp: event.timestamp,
              })
            }
          }
          break

        case 'tool':
          const toolData = event.data.content

          setStatusText(`${toolData.toolName} is working...`)
          if (
            toolData.toolName !== 'SuggestNextStepsTool' &&
            toolData.toolName !== 'AnalyzeDataTool'
          ) {
            updateOperation({
              id: toolData.toolId || event.id,
              toolName: toolData.toolName || 'unknown',
              status: toolData.status || 'unknown',
              timestamp: toolData.timestamp || event.timestamp,
              result: toolData.payload || null,
              rule: toolData.rule || null,
            })
          }
          break

        case 'tool_result':
          const toolResultData = event.data.content
          if (toolResultData.toolName === 'SuggestNextStepsTool') {
            const { assessment, options, sheetId } = toolResultData.result

            // First add the assessment message
            const assessmentMessage: ChatItem = {
              id: `${toolResultData.id || event.id}-assessment`,
              type: 'message',
              content: assessment,
              role: 'assistant',
              timestamp: event.timestamp,
            }

            // Then add the options message
            const optionsMessage: ChatItem = {
              id: `${toolResultData.id || event.id}-options`,
              type: 'options',
              content: options,
              options: options.map((option: any) => ({
                id: option.id,
                label: option.title,
                description: option.description,
                action: option.action,
                confidence: option.confidence,
              })),
              sheetId: sheetId, // Include the sheet ID with the options
              role: 'assistant',
              timestamp: event.timestamp,
            }
            setStatusText(`We have some fresh suggestions!`)
            addChatItem([assessmentMessage, optionsMessage])
          } else if (toolResultData.toolName === 'AnalyzeDataTool') {
            setStatusText(`Analyzing data...`)
            console.log('AnalyzeDataTool RESULT', toolResultData)
            addChatItem({
              id: `${toolResultData.id || event.id}-analysis`,
              type: 'message',
              content: toolResultData.result,
              role: 'assistant',
              timestamp: event.timestamp,
            })
          } else if (toolResultData.toolName === 'visualize_v2') {
            console.log('VisualizeToolV2 RESULT', toolResultData)

            const chartMessage: ChatItem = {
              id: `${toolResultData.id || event.id}-chart`,
              type: 'message',
              content: '',
              role: 'assistant',
              timestamp: event.timestamp,
              parts: [
                {
                  type: 'tool-invocation',
                  toolInvocation: {
                    toolName: 'chartPreview',
                    state: 'result',
                    result: toolResultData.result.data.result,
                  },
                },
              ],
            }

            addChatItem(chartMessage)
          } else {
            updateOperation({
              id: toolResultData.toolId || event.id,
              toolName: toolResultData.toolName || 'unknown',
              status: toolResultData.status || 'unknown',
              timestamp: toolResultData.timestamp || event.timestamp,
              result: toolResultData.payload || null,
              rule: toolResultData.rule || null,
            })
            window.postMessage({
              type: 'refetchRecords',
              sheetId: toolResultData.sheetId,
            })
          }
          setStatusText(`${toolResultData.toolName} finished!`)
          break

        case 'viz':
          const vizMessage: ChatItem = {
            id: `viz-${event.id}`,
            type: 'viz',
            content: '',
            role: 'assistant',
            timestamp: event.timestamp,
            parts: event.data.parts,
          }

          addChatItem(vizMessage)
          break

        case 'thread':
          const stateData = event.data.state
          if (stateData.currentRules) {
            updateRulesFromState(stateData.currentRules)
          }
          break

        case 'result':
          console.log('result', event)
          setStatusText('Prepare Chat Completed')
          setIsLoading(false)
          setStatus('online')
          break

        case 'artifact:updated':
        case 'artifact:created':
          console.log(`${event.type} event:`, event)
          // Invalidate artifact queries to refresh the UI
          if (event.data?.id) {
            console.log('invalidating artifacts query')
            queryClient.invalidateQueries({
              queryKey: ['artifacts'],
            })
            setStatusText('Artifact updated successfully')
          }
          break

        default:
          console.log('Unhandled event type:', event.type, event)
      }
    },
    [
      addChatItem,
      removeChatItem,
      updateOperation,
      updateRulesFromState,
      setStatusText,
      setIsLoading,
      setStatus,
    ]
  )

  /**
   * Handles error events
   */
  const handleError = useCallback(
    (error: Error) => {
      console.error('Error in prepare chat:', error)
      setIsLoading(false)
      setStatus('offline')
      threadStreamConnectedRef.current.connected = false
      projectStreamConnectedRef.current.connected = false
    },
    [setIsLoading, setStatus]
  )

  /**
   * Handles completion events
   */
  const handleComplete = useCallback(() => {
    console.warn('Prepare chat completed')
    setIsLoading(false)
    setStatus('online')
    threadStreamConnectedRef.current.connected = false
  }, [setIsLoading, setStatus])

  /**
   * Connects to a project stream
   */
  const connectToProjectStream = useCallback(
    async (currentProjectId: string) => {
      // Skip if no project ID
      if (!currentProjectId) return

      // In development mode, bypass any cooldown from StrictMode remounts
      if (process.env.NODE_ENV === 'development') {
        connectionManager.bypassConnectionCooldown(currentProjectId)
      }

      // Set our local ref for tracking
      projectStreamConnectedRef.current = {
        connected: true,
        projectId: currentProjectId,
      }

      try {
        // Always force a fresh connection when needed
        if (connectionManager.isConnected(currentProjectId)) {
          // Close existing connection first
          connectionManager.closeResource(currentProjectId)
          // Wait a moment for cleanup
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        // Subscribe through the connection manager
        const unsubscribe = await connectionManager.subscribe(
          currentProjectId,
          {
            onProgress: handleEvent,
            onError: handleError,
            onComplete: () => console.log('Project event stream completed'),
          }
        )

        // Store the unsubscribe function to be called when component unmounts
        return unsubscribe
      } catch (error) {
        console.error('Error connecting to project stream:', error)
        projectStreamConnectedRef.current.connected = false
        return () => {
          console.log(`Error cleanup for project ${currentProjectId}`)
        } // Return empty cleanup function
      }
    },
    [handleEvent, handleError]
  )

  // Connect to project events when projectId is available
  useEffect(() => {
    if (projectId && !projectStreamConnectedRef.current.connected) {
      // Try manually dispatching an event after connecting
      connectToProjectStream(projectId)
        .then(() => {})
        .catch((err) =>
          console.error(`Failed to connect to project stream: ${err.message}`)
        )
    }
  }, [
    projectId,
    connectToProjectStream,
    handleEvent,
    projectStreamConnectedRef,
  ])

  return {
    connectToProjectStream,
    handleError,
    handleComplete,
    threadStreamConnectedRef,
    projectStreamConnectedRef,
  }
}

export { normalizeEvent, useEventProcessing }
